import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MarketAvatar } from "../components/MarketAvatar";
import { MarketExternalLinks } from "../components/MarketExternalLinks";
import { MarketComments } from "../components/MarketComments";
import { MarketShareButton } from "../components/MarketShareButton";
import { MarketSocialProof } from "../components/MarketSocialProof";
import { MarketCardVotePanelSkeleton, MarketDetailSkeleton } from "../components/skeletons";
import { Seo } from "../components/Seo";
import { useMarketComments } from "../hooks/useMarketComments";
import { useMarketSocialProof } from "../hooks/useMarketSocialProof";
import { getMarket, startProfileXLink } from "../lib/api";
import { applyLiveMarketSnapshotToMarket } from "../lib/liveMarketSnapshots";
import { subscribeLive } from "../lib/liveRealtime";
import { marketRealtimeSubscriptionId } from "../lib/marketRealtime";
import { subscribeRealtime } from "../lib/realtime";
import { formatMarketTotalVotes } from "../lib/marketTokenDisplay.js";
import { formatUtcDateTime } from "../lib/timeDisplay";
import { useWalletAuth } from "../lib/walletAuth";
import { buildMarketSeo, buildSiteSeo } from "../../shared/seo";
import { genesisMarketBadgeDescription, genesisMarketBadgeLabel } from "../lib/genesisMarket";

const VotePanel = lazy(() => import("../components/VotePanel").then((module) => ({ default: module.VotePanel })));

export function MarketPage() {
  const { marketId } = useParams();
  const [market, setMarket] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [commentSort, setCommentSort] = useState("top");
  const marketRef = useRef(null);
  const { session } = useWalletAuth();
  const affiliateId = session?.walletAddress || session?.userId || "";
  const socialProof = useMarketSocialProof(market);
  const comments = useMarketComments({ marketId: market?.id || "", sort: commentSort });
  const targetCommentId = getTargetCommentId();
  const genesisBadgeLabel = genesisMarketBadgeLabel(market);
  const genesisBadgeDescription = genesisMarketBadgeDescription(market);

  const handleLinkX = useCallback(async () => {
    const redirectPath = `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`;
    const data = await startProfileXLink(redirectPath || "/profile");
    const authorizeUrl = typeof data?.authorizeUrl === "string" ? data.authorizeUrl.trim() : "";
    if (!authorizeUrl) throw new Error("Missing authorize URL");
    window.location.assign(authorizeUrl);
  }, []);

  const refresh = useCallback(async () => {
    if (!marketId) return;
    if (!marketRef.current) {
      setStatus("loading");
    }

    try {
      const data = await getMarket(marketId);
      marketRef.current = data;
      setMarket(data);
      setStatus("ready");
      setError("");
    } catch (caught) {
      if (!marketRef.current) {
        setStatus("error");
      }
      setError(caught?.message || "Could not load market.");
    }
  }, [marketId]);

  const handleLiveMarketUpdate = useCallback(
    (payload) => {
      const updatedMarket = applyLiveMarketSnapshotToMarket(marketRef.current, payload);
      if (updatedMarket && updatedMarket !== marketRef.current) {
        marketRef.current = updatedMarket;
        setMarket(updatedMarket);
        setStatus("ready");
        setError("");
        return;
      }
      refresh().catch(() => null);
    },
    [refresh]
  );

  useEffect(() => {
    refresh().catch(() => null);
  }, [refresh]);

  const liveMarketId = marketRealtimeSubscriptionId(market);

  useEffect(() => {
    if (!liveMarketId) return () => {};
    return subscribeRealtime({
      topics: ["market"],
      marketId: liveMarketId,
      onMarketUpdate: handleLiveMarketUpdate
    });
  }, [handleLiveMarketUpdate, liveMarketId]);

  useEffect(() => {
    if (!market?.id) return () => {};
    return subscribeLive({
      topics: ["comments"],
      marketId: market.id,
      onCommentsUpdate: comments.refresh
    });
  }, [comments.refresh, market?.id]);

  if (!market && status === "loading") {
    return (
      <section className="min-h-[126rem] space-y-4 md:min-h-[101rem]" data-route="market-detail">
        <MarketDetailSkeleton />
      </section>
    );
  }

  if (!market) {
    return (
      <>
        <Seo metadata={buildSiteSeo()} />
        <section className="space-y-4" data-route="market-detail">
          <article
            className="route-shell min-h-[126rem] space-y-4 md:min-h-[101rem]"
            data-cls-measure="market-detail-error"
          >
            <p className="text-sm font-semibold text-rose-200">Market unavailable.</p>
            <p className="max-w-2xl text-sm leading-6 text-zinc-300">
              {error || "This market could not be loaded. It may have been removed or the service may be unavailable."}
            </p>
          </article>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo metadata={buildMarketSeo({ market })} />
      <section className="space-y-4" data-route="market-detail">
        <article className="route-shell min-h-[126rem] space-y-4 md:min-h-[101rem]" data-cls-measure="market-detail">
          <header className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <MarketAvatar imageUrl={market.imageUrl} title={market.title} />
                <h2 className="text-2xl font-bold text-zinc-100">{market.title}</h2>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {genesisBadgeLabel ? (
                  <span
                    className="rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-1 text-center text-xs text-cyan-300"
                    title={genesisBadgeDescription || genesisBadgeLabel}
                  >
                    Genesis only
                  </span>
                ) : null}
                <span className="rounded-full border border-blue-500/40 px-2 py-1 text-center text-xs text-blue-400">
                  {formatMarketTotalVotes(market)}
                </span>
                <MarketShareButton market={market} affiliateId={affiliateId} />
              </div>
            </div>
            <p className="text-sm text-zinc-300">{market.summary}</p>
            <MarketExternalLinks xUrl={market.xUrl} websiteUrl={market.websiteUrl} />
            <p className="text-xs text-zinc-400">
              Voting window (UTC): {formatUtcDateTime(market.startAt)} - {formatUtcDateTime(market.endAt)}
            </p>
          </header>
          <Suspense fallback={<MarketCardVotePanelSkeleton />}>
            <VotePanel market={market} />
          </Suspense>
          <MarketSocialProof
            market={market}
            socialProof={socialProof.data}
            status={socialProof.status}
            error={socialProof.error}
            expectedOptionCount={market.options?.length || 4}
          />
          <MarketComments
            market={market}
            status={comments.status}
            commentsState={comments.data}
            error={comments.error}
            targetCommentId={targetCommentId}
            affiliateId={affiliateId}
            sort={commentSort}
            onSortChange={setCommentSort}
            onSubmit={comments.actions.create}
            onLike={comments.actions.like}
            onHide={comments.actions.hide}
            onReport={comments.actions.report}
            onLoadMore={comments.actions.loadMore}
            onLinkX={handleLinkX}
          />
        </article>
      </section>
    </>
  );
}

function getTargetCommentId() {
  if (typeof window === "undefined") return "";

  const params = new URLSearchParams(window.location.search);
  const queryTarget = params.get("comment");
  if (queryTarget) return queryTarget;

  const hash = String(window.location.hash || "");
  return hash.startsWith("#comment-") ? hash.replace(/^#comment-/, "") : "";
}
